import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { downloadBlob, errMsg } from "../ui";
import { CompareBar } from "./CompareBar";
import { DomainRow } from "./DomainRow";
import { STATUS, STATUS_ORDER, money, num } from "./meta";

const PAGE = 50;

function Tiles({ usage, status, onPick }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7" data-testid="uc-status-tiles">
      {STATUS_ORDER.filter((s) => usage.counts[s] || s === "would_update" || s === "unchanged").map((s) => (
        <button key={s} type="button" onClick={() => onPick(status === s ? "" : s)} data-testid={`uc-tile-${s}`}
                className={`rounded-lg border px-3 py-2 text-left transition-colors ${status === s ? "border-slate-900 bg-slate-900 text-white" : `${STATUS[s].tone} hover:border-slate-400`}`}>
          <span className="block text-[11px] font-medium">{STATUS[s].label}</span>
          <span className="block font-mono text-lg font-semibold tabular-nums">{num(usage.counts[s])}</span>
          <span className="block font-mono text-[11px] tabular-nums opacity-80">{money(usage.amounts[s])}</span>
        </button>))}
    </div>
  );
}

/** The open cycle's usage per domain for one run: what Stax Bill has, what it gets (or got). */
export function RunDomains({ run }) {
  const u = run.usage;
  const [f, setF] = usePersistedState("usage-run-domains.f", { status: "", q: "", sort: "amount", jump: false, review: "" });
  const [tick, setTick] = useState(0);
  const [reviewed, setReviewed] = useState(run.reviews?.reviewed || 0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const seq = useRef(0);
  useEffect(() => {
    const t = setTimeout(() => {
      const mine = ++seq.current;
      api.get(`/usage-charges/runs/${run.id}/domains`, { params: { ...f, review: f.jump ? f.review : "", page, page_size: PAGE } })
        .then(({ data: d }) => { if (mine === seq.current) setData(d); })
        .catch((e) => toast.error(errMsg(e, "Could not load the domains")));
    }, f.q ? 250 : 0);
    return () => clearTimeout(t);
  }, [run.id, f, page, tick]);
  const onReviewed = (delta) => { setReviewed((n) => n + delta); setTick((t) => t + 1); };
  const set = (k, v) => { setPage(1); setF({ ...f, [k]: v }); };
  const exportCsv = async () => {
    try { const { data: b } = await api.get(`/usage-charges/runs/${run.id}/domains.csv`, { params: { status: f.status, q: f.q, jump: f.jump, review: f.jump ? f.review : "" }, responseType: "blob" }); downloadBlob(b, `usage_${run.cycle?.key}_${run.id}.csv`); }
    catch (e) { toast.error("Could not export"); }
  };
  const pages = data ? Math.ceil(data.total / PAGE) : 1;
  return (
    <div className="space-y-3" data-testid="uc-run-domains">
      <div className="flex flex-wrap items-end gap-3">
        <div><div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Cycle usage {u.mode === "live" ? "written to Stax Bill" : "— dry run, Stax Bill not changed"}</div>
          <div className="font-mono text-2xl font-semibold tabular-nums text-slate-900" data-testid="uc-run-total">{money(u.total)}</div>
          <div className="text-xs text-slate-500">{num(u.domains)} domains with calls · {num(u.stax_calls)} Stax Bill calls</div></div>
        <span className={`mb-1 rounded px-2 py-0.5 text-[11px] font-bold ${u.mode === "live" ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700"}`} data-testid="uc-run-mode">{u.mode === "live" ? "LIVE" : "DRY RUN"}</span>
      </div>
      <Tiles usage={u} status={f.status} onPick={(s) => set("status", s)} />
      <CompareBar run={run} f={f} set={set} reviewed={reviewed} />
      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="relative w-64"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search domain, territory, customer…" className="h-9 pl-8 text-xs" data-testid="uc-domains-search" /></div>
          <select value={f.sort} onChange={(e) => set("sort", e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="uc-domains-sort">
            <option value="amount">Biggest amount first</option><option value="change">Biggest change vs last cycle</option>
            <option value="calls">Most calls first</option><option value="domain">Domain A → Z</option></select>
          {data && <span className="text-xs text-slate-500" data-testid="uc-domains-total">{num(data.total)} domains</span>}
          {run.can_export && <Button size="sm" variant="outline" className="ml-auto" onClick={exportCsv} data-testid="uc-domains-export"><Download className="h-3.5 w-3.5" /> Export CSV</Button>}
        </div>
        <div className="max-h-[62vh] overflow-auto">
          {!data ? <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-slate-300" /> : !data.rows.length
            ? <p className="p-10 text-center text-sm text-slate-500" data-testid="uc-domains-empty">No domains here.</p> : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white"><tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 font-semibold">Domain</th><th className="px-3 py-2 font-semibold">Customer / subscription</th>
                  <th className="px-3 py-2 text-right font-semibold">Calls</th><th className="px-3 py-2 text-right font-semibold">On Stax Bill</th>
                  <th className="px-3 py-2 text-right font-semibold">Cycle amount</th><th className="px-3 py-2 text-right font-semibold">Last cycle</th>
                  <th className="px-3 py-2 font-semibold">Status</th>{u.jumps > 0 && <th className="px-3 py-2 font-semibold">Review</th>}</tr></thead>
                <tbody>{data.rows.map((r) => <DomainRow key={r.domain} r={r} cycle={run.cycle} labels={(u.trend_cycles || []).map((c) => c.label)}
                                                        review={u.jumps > 0 ? { canReview: run.can_edit, onDone: onReviewed } : null} />)}</tbody>
              </table>)}
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
            Page {page} of {pages}
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="uc-domains-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="uc-domains-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>)}
      </Card>
    </div>
  );
}
